// Bulk-import per-feature specs from a folder on disk. Matches files to
// features by GUID substring: a file whose basename contains a feature's
// candidateId becomes that feature's spec markdown. When multiple feature
// ids would match one filename (prefix collisions), the LONGEST match wins.
//
// Used at two levels:
//   - Delivery view: scope = every feature in the session.
//   - MU drill-down Specs tab: scope = features in that MU only.
//
// File reads + the matching pass happen entirely client-side. Each match
// then PUTs to /api/sessions/:id/feature-specs/:featureId (the same
// endpoint the in-app markdown editor uses), so imported specs are
// flagged `userEdited: true` and protected from accidental AI overwrite.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Folder, AlertCircle, CheckCircle2, FolderOpen, Upload,
} from 'lucide-react';
import type {
  DiscoveryFeature,
  FeatureSpecsPayload,
  FeatureSpec,
} from '../hooks/sessionTypes';
import '../styles/DeliveryCsvImportModal.css';
import '../styles/SpecFolderImportModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Features in scope (every-MU or single-MU depending on entry point).
  features: DiscoveryFeature[];
  specsPayload: FeatureSpecsPayload;
  // Save callback. Returns the saved spec record on success, null on failure.
  onSave: (featureId: string, markdown: string) => Promise<FeatureSpec | null>;
  // Used for the modal header context.
  scopeLabel?: string;
}

interface FilePick {
  file: File;
  text: string;
}

interface Match {
  feature: DiscoveryFeature;
  file: File;
  alreadyHasSpec: boolean;
  userEdited: boolean;
}

const ACCEPTED_EXTS = ['.md', '.markdown', '.txt'];

/** Normalize a path for comparison: lowercase, forward slashes, no leading `./`. */
function normalizePath(p: string): string {
  return (p || '').toLowerCase().replace(/\\/g, '/').replace(/^\.?\//, '');
}

/**
 * True when two paths represent the same file regardless of which side
 * carries the longer prefix. Handles:
 *   filePath = "delivery/specs/auth/_bc.md", specPath = "specs/auth/_bc.md"  → match
 *   filePath = "auth/_bc.md",                specPath = "specs/auth/_bc.md"  → match
 *   filePath = "auth/_bc.md",                specPath = "auth/_bc.md"        → match
 */
function pathSuffixMatch(filePath: string, specPath: string): boolean {
  const f = normalizePath(filePath);
  const s = normalizePath(specPath);
  if (!f || !s) return false;
  if (f === s) return true;
  if (f.endsWith('/' + s)) return true;
  if (s.endsWith('/' + f)) return true;
  return false;
}

const SpecFolderImportModal: React.FC<Props> = ({
  isOpen, onClose, features, specsPayload, onSave, scopeLabel,
}) => {
  const [picks, setPicks] = useState<FilePick[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ imported: number; skipped: number } | null>(null);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setPicks([]);
    setOverwrite(false);
    setBusy(false);
    setProgress(null);
    setError(null);
    setDone(null);
  }, [isOpen]);

  const handlePick = useCallback(async (filesIn: FileList | null) => {
    if (!filesIn) return;
    setError(null);
    const out: FilePick[] = [];
    const skipped: string[] = [];
    for (let i = 0; i < filesIn.length; i++) {
      const f = filesIn[i];
      const name = f.name.toLowerCase();
      if (!ACCEPTED_EXTS.some((ext) => name.endsWith(ext))) {
        skipped.push(f.name);
        continue;
      }
      if (f.size > 200_000) {
        skipped.push(`${f.name} (>200KB)`);
        continue;
      }
      try {
        const text = await f.text();
        out.push({ file: f, text });
      } catch {
        skipped.push(`${f.name} (read failed)`);
      }
    }
    if (skipped.length > 0) {
      setError(`Skipped ${skipped.length} non-markdown / oversized file(s): ${skipped.slice(0, 4).join(', ')}${skipped.length > 4 ? ', …' : ''}`);
    }
    setPicks(out);
  }, []);

  // Match each picked file to a feature, preferring `spec_path` when the
  // feature has one. Falls back to GUID substring match when the feature
  // has no spec_path. Path matching uses suffix equality so the picker
  // works whether the user selected the parent folder of `specs/` or
  // a specific subfolder underneath.
  const { matches, unmatched, featuresWithoutFile } = useMemo(() => {
    if (picks.length === 0) {
      return { matches: [] as Match[], unmatched: [] as File[], featuresWithoutFile: [] as DiscoveryFeature[] };
    }

    // Index features by spec_path (normalized) and by candidateId for fast lookup.
    // We sort by spec_path length descending so longer specs (more specific
    // paths) win when there's any ambiguity.
    const featuresBySpecPath = [...features]
      .filter((f) => !!f.specPath)
      .sort((a, b) => normalizePath(b.specPath!).length - normalizePath(a.specPath!).length);
    const idsByLength = [...features]
      .map((f) => f.candidateId)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);
    const featureById = new Map(features.map((f) => [f.candidateId, f]));
    const matchedFeatures = new Set<string>();
    const matches: Match[] = [];
    const unmatched: File[] = [];

    for (const pick of picks) {
      // webkitRelativePath includes the folder structure when the user picked
      // a directory; falls back to plain filename when they used the
      // multi-file picker.
      const relPath = (pick.file as File & { webkitRelativePath?: string }).webkitRelativePath
        || pick.file.name;

      // Path-based match (preferred when feature has a spec_path).
      let feature: DiscoveryFeature | null = null;
      for (const f of featuresBySpecPath) {
        if (pathSuffixMatch(relPath, f.specPath!)) { feature = f; break; }
      }

      // GUID-substring fallback for features that have no spec_path.
      if (!feature) {
        for (const id of idsByLength) {
          if (pick.file.name.includes(id)) {
            const candidate = featureById.get(id)!;
            // Skip features that already have spec_path — they should match
            // by path, not by GUID-in-filename coincidence.
            if (candidate.specPath) continue;
            feature = candidate;
            break;
          }
        }
      }

      if (!feature) {
        unmatched.push(pick.file);
        continue;
      }
      if (matchedFeatures.has(feature.candidateId)) {
        // Multiple files matched the same feature — prefer the first by
        // sort order. Subsequent ones surface as unmatched so the user
        // sees the ambiguity.
        unmatched.push(pick.file);
        continue;
      }
      const existing = specsPayload.specs[feature.candidateId];
      matches.push({
        feature,
        file: pick.file,
        alreadyHasSpec: !!existing?.markdown,
        userEdited: !!existing?.userEdited,
      });
      matchedFeatures.add(feature.candidateId);
    }
    const featuresWithoutFile = features.filter((f) => !matchedFeatures.has(f.candidateId));
    return { matches, unmatched, featuresWithoutFile };
  }, [picks, features, specsPayload.specs]);

  const willOverwriteCount = matches.filter((m) => m.alreadyHasSpec && !overwrite).length;
  const importableCount = matches.filter((m) => overwrite || !m.alreadyHasSpec).length;

  const handleImport = useCallback(async () => {
    if (matches.length === 0 || importableCount === 0) return;
    setBusy(true);
    setError(null);
    let imported = 0;
    let skipped = 0;
    const targets = overwrite ? matches : matches.filter((m) => !m.alreadyHasSpec);
    setProgress({ done: 0, total: targets.length });
    for (const m of targets) {
      const pick = picks.find((p) => p.file === m.file);
      if (!pick) { skipped += 1; continue; }
      const result = await onSave(m.feature.candidateId, pick.text);
      if (result) imported += 1; else skipped += 1;
      setProgress((prev) => prev ? { done: prev.done + 1, total: prev.total } : prev);
    }
    setProgress(null);
    setBusy(false);
    setDone({ imported, skipped: skipped + (matches.length - targets.length) });
  }, [matches, importableCount, overwrite, picks, onSave]);

  if (!isOpen) return null;

  return (
    <div className="dv-modal-backdrop" onClick={onClose}>
      <div className="dv-modal sfi-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dv-modal-head">
          <h3>Import specs from folder{scopeLabel ? ` — ${scopeLabel}` : ''}</h3>
          <button type="button" className="dv-modal-close" onClick={onClose}><X size={14} /></button>
        </header>

        <div className="dv-modal-body">
          {done ? (
            <DoneView done={done} onClose={onClose} />
          ) : (
            <>
              {error && (
                <div className="dv-modal-error" role="alert">
                  <AlertCircle size={14} /> {error}
                </div>
              )}

              {picks.length === 0 ? (
                <FolderPicker onPick={handlePick} />
              ) : (
                <MatchPreview
                  matches={matches}
                  unmatched={unmatched}
                  featuresWithoutFile={featuresWithoutFile}
                  overwrite={overwrite}
                  setOverwrite={setOverwrite}
                  willOverwriteCount={willOverwriteCount}
                  onPickAgain={() => setPicks([])}
                />
              )}

              <footer className="dv-modal-foot">
                <button type="button" className="dv-btn dv-btn--ghost" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="dv-btn dv-btn--primary"
                  onClick={handleImport}
                  disabled={busy || importableCount === 0}
                >
                  {busy
                    ? progress ? `Importing ${progress.done}/${progress.total}…` : 'Importing…'
                    : `Import ${importableCount} spec${importableCount === 1 ? '' : 's'}`}
                </button>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecFolderImportModal;

// ── Sub-views ─────────────────────────────────────────────────────

const FolderPicker: React.FC<{ onPick: (files: FileList | null) => void }> = ({ onPick }) => {
  // Use a ref-less label-and-input combo so we don't have to manage focus.
  // The non-standard `webkitdirectory` lets the OS file picker offer a
  // folder selection. We fall through to multi-file pick for browsers
  // that ignore it.
  return (
    <div className="dv-dropzone sfi-dropzone">
      <FolderOpen size={28} />
      <p className="dv-dropzone-title">Choose a folder of spec files</p>
      <p className="dv-dropzone-sub">
        Files match features by <code>spec_path</code> when present (suffix match against the file's
        relative path), otherwise by GUID substring on the filename. Accepted:
        <code>.md</code>, <code>.markdown</code>, <code>.txt</code>. Files larger than 200KB are skipped.
      </p>
      <div className="sfi-picker-actions">
        <label className="dv-btn dv-btn--primary">
          <Folder size={13} /> Pick folder
          <input
            type="file"
            multiple
            // @ts-expect-error — non-standard but widely supported
            webkitdirectory=""
            directory=""
            style={{ display: 'none' }}
            onChange={(e) => onPick(e.target.files)}
            accept=".md,.markdown,.txt"
          />
        </label>
        <label className="dv-btn dv-btn--ghost">
          <Upload size={13} /> Pick files
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            accept=".md,.markdown,.txt,text/markdown,text/plain"
            onChange={(e) => onPick(e.target.files)}
          />
        </label>
      </div>
    </div>
  );
};

interface PreviewProps {
  matches: Match[];
  unmatched: File[];
  featuresWithoutFile: DiscoveryFeature[];
  overwrite: boolean;
  setOverwrite: (v: boolean) => void;
  willOverwriteCount: number;
  onPickAgain: () => void;
}

const MatchPreview: React.FC<PreviewProps> = ({
  matches, unmatched, featuresWithoutFile, overwrite, setOverwrite, willOverwriteCount, onPickAgain,
}) => (
  <>
    <div className="dv-preview-summary">
      <CheckCircle2 size={14} />
      <span>
        <strong>{matches.length}</strong> matched
        {' · '}
        <strong>{featuresWithoutFile.length}</strong> unmatched
        <span className="sfi-summary-hint"> (of {matches.length + featuresWithoutFile.length} features in this scope)</span>
        {unmatched.length > 0 && (
          <> · <span className="sfi-summary-muted">{unmatched.length} file{unmatched.length === 1 ? '' : 's'} out of scope</span></>
        )}
      </span>
      <button type="button" className="sfi-pick-again" onClick={onPickAgain}>Pick different files</button>
    </div>

    {matches.length > 0 && (
      <div className="sfi-list">
        <h4>Matches</h4>
        <ul>
          {matches.slice(0, 20).map((m) => (
            <li key={m.feature.candidateId} className={m.alreadyHasSpec ? 'sfi-row sfi-row--overwrite' : 'sfi-row'}>
              <span className="sfi-feature">
                <strong>{m.feature.name}</strong>
                <code>{m.feature.specPath || m.feature.candidateId}</code>
              </span>
              <span className="sfi-arrow">←</span>
              <span className="sfi-file" title={(m.file as File & { webkitRelativePath?: string }).webkitRelativePath || m.file.name}>
                {(m.file as File & { webkitRelativePath?: string }).webkitRelativePath || m.file.name}
              </span>
              {m.alreadyHasSpec && (
                <span className={`sfi-pill ${m.userEdited ? 'sfi-pill--edit' : 'sfi-pill--ai'}`}>
                  {m.userEdited ? 'user-edited' : 'AI-drafted'}
                </span>
              )}
            </li>
          ))}
          {matches.length > 20 && <li className="sfi-more">…+{matches.length - 20} more</li>}
        </ul>
      </div>
    )}

    {(willOverwriteCount > 0 || matches.some((m) => m.alreadyHasSpec)) && (
      <label className="sfi-overwrite-toggle">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
        />
        Overwrite features that already have specs ({matches.filter((m) => m.alreadyHasSpec).length})
      </label>
    )}

    {featuresWithoutFile.length > 0 && (
      <details className="sfi-list" open={featuresWithoutFile.length <= 8}>
        <summary>Unmatched features ({featuresWithoutFile.length})</summary>
        <ul>
          {featuresWithoutFile.slice(0, 20).map((f) => (
            <li key={f.candidateId}><strong>{f.name}</strong> — <code>{f.candidateId}</code></li>
          ))}
          {featuresWithoutFile.length > 20 && <li className="sfi-more">…+{featuresWithoutFile.length - 20} more</li>}
        </ul>
      </details>
    )}

    {unmatched.length > 0 && (
      <details className="sfi-list">
        <summary>Files out of scope — likely for other MUs ({unmatched.length})</summary>
        <ul>
          {unmatched.slice(0, 20).map((f) => <li key={f.name}>{f.name}</li>)}
          {unmatched.length > 20 && <li className="sfi-more">…+{unmatched.length - 20} more</li>}
        </ul>
      </details>
    )}
  </>
);

const DoneView: React.FC<{ done: { imported: number; skipped: number }; onClose: () => void }> = ({ done, onClose }) => (
  <div className="sfi-done">
    <CheckCircle2 size={28} />
    <h3>Done</h3>
    <p>
      Imported <strong>{done.imported}</strong> spec{done.imported === 1 ? '' : 's'}.
      {done.skipped > 0 && <> Skipped {done.skipped}.</>}
    </p>
    <button type="button" className="dv-btn dv-btn--primary" onClick={onClose}>Close</button>
  </div>
);
